"use client";

import { useEffect, useMemo, useRef, useState } from "react";

type MagnifierProps = {
  windowId?: string;
  lensDiameterPx?: number; // fixed lens diameter
  scale?: number; // visual scale for text rendering
};

// (reserved for future heuristics for text extraction)

export default function Magnifier({
  lensDiameterPx = 200,
  scale = 1.8,
}: MagnifierProps) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isHovering, setIsHovering] = useState(false);
  const [lensSize, setLensSize] = useState<{ w: number; h: number } | null>(
    null
  );
  const [snapshotDoc, setSnapshotDoc] = useState<string | null>(null);
  const [isFrameReady, setIsFrameReady] = useState(false);

  // Refs to avoid re-render on mouse move
  const focusRef = useRef<{ x: number; y: number } | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const lastSnapshotAtRef = useRef<number>(0);
  const refreshScheduledRef = useRef<boolean>(false);
  const isRefreshingRef = useRef<boolean>(false);

  // Initialize focus so lens shows something meaningful on open
  useEffect(() => {
    if (!focusRef.current) {
      focusRef.current = {
        x: window.scrollX + window.innerWidth / 2,
        y: window.scrollY + window.innerHeight / 2,
      };
    }
  }, []);

  // Observe lens size to compute lens-local center
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      setLensSize({ w: cr.width, h: cr.height });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  function snapshotCurrentDocument(): string | null {
    try {
      const cloned = document.documentElement.cloneNode(true) as HTMLElement;
      // Remove the magnifier subtree to avoid recursion
      cloned
        .querySelectorAll('[data-magnifier-root="1"]')
        .forEach((n) => n.remove());
      // Do not run scripts inside the iframe snapshot
      cloned.querySelectorAll("script").forEach((n) => n.remove());
      // Ensure relative URLs resolve correctly
      const headEl = cloned.querySelector("head");
      if (headEl) {
        const base = document.createElement("base");
        base.setAttribute("href", window.location.href);
        headEl.insertBefore(base, headEl.firstChild);
      }
      return "<!doctype html>" + (cloned as HTMLElement).outerHTML;
    } catch {
      return null;
    }
  }

  // Build a static snapshot of current DOM so all layers/tags are included
  useEffect(() => {
    if (snapshotDoc) return;
    const html = snapshotCurrentDocument();
    if (html) setSnapshotDoc(html);
  }, [snapshotDoc]);

  // RAF loop to apply transform without causing React re-renders
  useEffect(() => {
    const loop = () => {
      const frame = iframeRef.current;
      const size = lensSize;
      const focus = focusRef.current;
      if (frame && size && focus && isFrameReady) {
        const lensLocalCX = size.w / 2;
        const lensLocalCY = size.h / 2;
        const translateX = lensLocalCX - focus.x * scale;
        const translateY = lensLocalCY - focus.y * scale;
        const docEl = frame.contentDocument
          ?.documentElement as HTMLElement | null;
        if (docEl) {
          docEl.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
          docEl.style.transformOrigin = "0 0";
        }
        const body = frame.contentDocument?.body as HTMLElement | null;
        if (body) {
          body.style.margin = "0";
        }
      }
      rafIdRef.current = window.requestAnimationFrame(loop);
    };
    rafIdRef.current = window.requestAnimationFrame(loop);
    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
    };
  }, [lensSize, scale, isFrameReady]);

  const lensStyle = useMemo<React.CSSProperties>(() => {
    const r = lensDiameterPx;
    return {
      width: r,
      height: r,
      borderRadius: 20, // Apple-style rounded corners
      // Remove circular mask for square design
    } as React.CSSProperties;
  }, [lensDiameterPx]);

  return (
    <div
      ref={rootRef}
      className="relative h-full w-full select-none"
      style={{ background: "transparent" }}
      aria-label="Text magnifier lens"
      onMouseEnter={(e) => {
        setIsHovering(true);
        const pageX = window.scrollX + e.clientX;
        const pageY = window.scrollY + e.clientY;
        focusRef.current = { x: pageX, y: pageY };
        // Debounced snapshot refresh on re-entry (min interval 800ms)
        const now = Date.now();
        if (
          !refreshScheduledRef.current &&
          !isRefreshingRef.current &&
          now - lastSnapshotAtRef.current > 800
        ) {
          refreshScheduledRef.current = true;
          type WindowWithRIC = Window & {
            requestIdleCallback?: (
              cb: (deadline: {
                timeRemaining: () => number;
                didTimeout: boolean;
              }) => void,
              opts?: { timeout: number }
            ) => number;
          };
          const win = window as WindowWithRIC;
          const schedule = (cb: () => void) =>
            typeof win.requestIdleCallback === "function"
              ? win.requestIdleCallback(() => cb(), { timeout: 500 })
              : window.setTimeout(cb, 0);
          schedule(() => {
            const html = snapshotCurrentDocument();
            if (html) {
              isRefreshingRef.current = true;
              // Don't set isFrameReady to false - keep showing old content
              setSnapshotDoc(html);
              lastSnapshotAtRef.current = Date.now();
            }
            refreshScheduledRef.current = false;
          });
        }
      }}
      onMouseMove={(e) => {
        if (!isHovering) return;
        const pageX = window.scrollX + e.clientX;
        const pageY = window.scrollY + e.clientY;
        focusRef.current = { x: pageX, y: pageY };
      }}
      onMouseLeave={() => {
        setIsHovering(false);
        // Freeze last focus point when leaving
      }}
    >
      {/* Square lens with Apple glass design using an embedded snapshot of the page */}
      <div
        className="absolute inset-0 overflow-hidden shadow-2xl"
        style={{
          ...lensStyle,
          border: "1px solid rgba(255, 255, 255, 0.18)",
          boxShadow:
            "0 8px 32px 0 rgba(0, 0, 0, 0.37), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)",
          background: "rgba(255, 255, 255, 0.01)",
        }}
        data-magnifier-root="1"
      >
        <iframe
          ref={iframeRef}
          title="magnifier-embed"
          srcDoc={snapshotDoc ?? undefined}
          onLoad={() => {
            setIsFrameReady(true);
            isRefreshingRef.current = false;
          }}
          style={{
            width: "100vw",
            height: "100vh",
            border: 0,
            position: "absolute",
            top: 0,
            left: 0,
            backgroundColor: "var(--color-background)",
            pointerEvents: "none",
          }}
        />
      </div>
    </div>
  );
}
